// src/pages/ReportsPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Container,
  Row,
  Col,
  Table,
  ProgressBar,
  Badge,
  Button,
  Form,
  Modal,
  InputGroup,
  Spinner
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import reportApi from "../../../data/api/reportApi.js";

/* ------------------------------ helpers ------------------------------ */

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// kiterjesztés felismerés fájlnévből
function inferFormatFromFilename(name = "") {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".xlsx") || n.endsWith(".xls")) return "XLSX";
  if (n.endsWith(".csv")) return "CSV";
  if (n.endsWith(".pdf")) return "PDF";
  if (n.endsWith(".html") || n.endsWith(".htm")) return "HTML";
  if (n.endsWith(".xml")) return "XML";
  return "";
}

// export formátumok összeállítása: CSV/XLSX mindig, + sablonokból
function computeAvailableFormats(tpl) {
  //  const set = new Set(["CSV", "XLSX"]);
  const set = new Set(["XLSX"]);
  // Report.extensions (ha használtok ilyet)
  (tpl?.formats || []).forEach((f) => set.add(String(f).toUpperCase()));
  // Feltöltött sablonokból jövő formátumok
  (tpl?.templateFormats || []).forEach((f) => set.add(String(f).toUpperCase()));
  // Ha van HTML sablon, akkor PDF is előállítható belőle
  if (set.has("HTML")) set.add("PDF");
  return Array.from(set);
}

// UI -> backend string builder helpers
function buildParameterTypeString(params = []) {
  console.log("params: ", params);
  return params
    .map((p, i) => {
      const idx = i + 1;
      const t =
        p.type === "dateRange"
          ? "DateRange"
          : p.type === "numberRange"
          ? "NumberRange"
          : p.type === "enum"
          ? "Enum"
          : p.type === "list"
          ? "List"
          : p.type === "number"
          ? "Number"
          : p.type === "date"
          ? "Date"
          : p.type === "checkbox"
          ? "Checkbox"
          : "Text";
      return `${idx}:${t}`;
    })
    .join(",");
}
function buildParameterNameString(params = []) {
  return params
    .map((p, i) => `${i + 1}:${p.label || `Param ${i + 1}`}`)
    .join(",");
}
function buildParameterDefaultString(params = []) {
  return params
    .map((p, i) => {
      const idx = i + 1;
      // tartományoknál nem írunk defaultot a stringbe
      if (p.type === "dateRange" || p.type === "numberRange") return null;
      if (p.type === "list") {
        const arr = Array.isArray(p.default) ? p.default : [];
        return `${idx}:${arr.join(",")}`;
      }
      if (p.type === "checkbox") return `${idx}:${String(!!p.default)}`;
      if (p.type === "number") {
        return (p.default ?? "") !== "" && p.default != null
          ? `${idx}:${String(p.default)}`
          : `${idx}:`;
      }
      // text, date, enum
      return `${idx}:${p.default ?? ""}`;
    })
    .filter(Boolean)
    .join("|");
}
function buildEnumPairsString(params = []) {
  return params
    .map((p, i) => {
      if (p.type !== "enum" && p.type !== "list") return null;
      const idx = i + 1;
      const opts = (p.options || []).map((o) => `${o.label}-${o.id}`).join(",");
      return `${idx}:${opts}`;
    })
    .filter(Boolean)
    .join(" | ");
}

/* -------------------------- Reusable UI -------------------------- */

const PARAM_TYPES = [
  { value: "dateRange", label: "Dátum intervallum" },
  { value: "numberRange", label: "Szám intervallum" },
  { value: "date", label: "Dátum" },
  { value: "number", label: "Szám" },
  { value: "text", label: "Szöveg" },
  { value: "enum", label: "Választó (egyválasztós)" },
  { value: "list", label: "Lista (többválasztós)" },
  { value: "checkbox", label: "Jelölőnégyzet" }
];

function ParametersForm({ template, value, onChange }) {
  const setField = (key, v) => onChange({ ...value, [key]: v });

  return (
    <div className="d-grid gap-2">
      {(template?.parameters || []).map((p) => {
        const v = value?.[p.key];
        console.log("Rendering parameter: ", p.key, v);
        console.log("p.type: ", p.type);
        console.log("template: ", template);
        if (p.type == "dateRange") {
          const from = v?.from || "";
          const to = v?.to || "";
          return (
            <Row key={p.key}>
              <Col>
                <Form.Label>{p.label} – Kezdő</Form.Label>
                <Form.Control
                  type="date"
                  value={from}
                  onChange={(e) =>
                    setField(p.key, { ...v, from: e.target.value })
                  }
                />
              </Col>
              <Col>
                <Form.Label>{p.label} – Vég</Form.Label>
                <Form.Control
                  type="date"
                  value={to}
                  onChange={(e) =>
                    setField(p.key, { ...v, to: e.target.value })
                  }
                />
              </Col>
            </Row>
          );
        }

        if (p.type == "numberRange") {
          const from = v?.from ?? "";
          const to = v?.to ?? "";
          return (
            <Row key={p.key}>
              <Col>
                <Form.Label>{p.label} – Minimum</Form.Label>
                <Form.Control
                  type="number"
                  value={from}
                  onChange={(e) =>
                    setField(
                      p.key,
                      e.target.value === ""
                        ? { ...v, from: "" }
                        : { ...v, from: Number(e.target.value) }
                    )
                  }
                />
              </Col>
              <Col>
                <Form.Label>{p.label} – Maximum</Form.Label>
                <Form.Control
                  type="number"
                  value={to}
                  onChange={(e) =>
                    setField(
                      p.key,
                      e.target.value === ""
                        ? { ...v, to: "" }
                        : { ...v, to: Number(e.target.value) }
                    )
                  }
                />
              </Col>
            </Row>
          );
        }

        if (p.type === "enum") {
          return (
            <div key={p.key}>
              <Form.Label>{p.label}</Form.Label>
              <Form.Select
                value={v ?? p.default ?? ""}
                onChange={(e) => setField(p.key, e.target.value)}
              >
                {(p.options || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </div>
          );
        }

        if (p.type === "list") {
          const selected = Array.isArray(v)
            ? v
            : Array.isArray(p.default)
            ? p.default
            : [];
          return (
            <div key={p.key}>
              <Form.Label>{p.label}</Form.Label>
              <Form.Select
                multiple
                value={selected}
                onChange={(e) =>
                  setField(
                    p.key,
                    Array.from(e.target.selectedOptions).map((o) => o.value)
                  )
                }
              >
                {(p.options || []).map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <div className="text-muted" style={{ fontSize: ".85rem" }}>
                Több elem Ctrl/⌘ vagy Shift segítségével jelölhető.
              </div>
            </div>
          );
        }

        if (p.type === "date") {
          return (
            <div key={p.key}>
              <Form.Label>{p.label}</Form.Label>
              <Form.Control
                type="date"
                value={v ?? p.default ?? ""}
                onChange={(e) => setField(p.key, e.target.value)}
              />
            </div>
          );
        }

        if (p.type === "number") {
          return (
            <div key={p.key}>
              <Form.Label>{p.label}</Form.Label>
              <Form.Control
                type="number"
                value={v ?? p.default ?? ""}
                onChange={(e) =>
                  setField(
                    p.key,
                    e.target.value === "" ? "" : Number(e.target.value)
                  )
                }
              />
            </div>
          );
        }

        if (p.type === "checkbox") {
          return (
            <Form.Check
              key={p.key}
              type="checkbox"
              label={p.label}
              checked={!!(v ?? p.default ?? false)}
              onChange={(e) => setField(p.key, e.target.checked)}
            />
          );
        }

        // text
        return (
          <div key={p.key}>
            <Form.Label>{p.label}</Form.Label>
            <Form.Control
              type="text"
              value={v ?? p.default ?? ""}
              onChange={(e) => setField(p.key, e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
}

function ReportExportPanel({ templates = [], onExport }) {
  const [selectedId, setSelectedId] = useState("");
  const selected = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedId)),
    [selectedId, templates]
  );
  const [params, setParams] = useState({});
  const [format, setFormat] = useState("XLSX");

  const availableFormats = useMemo(
    () => computeAvailableFormats(selected),
    [selected]
  );

  useEffect(() => {
    if (!templates.length) return;
    setSelectedId(String(templates[0].id));
  }, [templates]);

  useEffect(() => {
    if (!selected) return;
    setParams(
      (selected.parameters || []).reduce((acc, p) => {
        if (p.type === "dateRange" || p.type === "numberRange") {
          acc[p.key] = { from: "", to: "" };
        } else if (p.type === "list") {
          acc[p.key] = Array.isArray(p.default) ? p.default : [];
        } else if (p.type === "checkbox") {
          acc[p.key] = !!p.default;
        } else {
          acc[p.key] = p.default ?? "";
        }
        return acc;
      }, {})
    );
    setFormat(availableFormats[0] || "XLSX");
  }, [selected, availableFormats]);

  const handleExportClick = () => {
    if (!selected || !format) return;
    onExport?.(selected.id, format, params);
  };

  if (!templates.length) {
    return (
      <Card className="shadow-sm mb-4">
        <Card.Body>Nem található report sablon.</Card.Body>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm mb-4">
      <Card.Body>
        <Card.Title className="mb-3">
          <i className="bi bi-download me-2" />
          Report export
        </Card.Title>

        <Row className="g-3">
          <Col lg={4}>
            <Form.Label>Report</Form.Label>
            <Form.Select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id}
                </option>
              ))}
            </Form.Select>
            <div className="text-muted mt-2" style={{ fontSize: ".9rem" }}>
              {selected?.description}
            </div>
          </Col>

          <Col lg={5}>
            <ParametersForm
              template={selected}
              value={params}
              onChange={setParams}
            />
          </Col>

          <Col lg={3}>
            <Form.Label>Formátum</Form.Label>
            <Form.Select
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              {availableFormats.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Form.Select>
            <Button className="mt-3 w-100" onClick={handleExportClick}>
              <i className="bi bi-file-earmark-arrow-down me-2" />
              Exportálás
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function AdminReportDesigner({ templates, onCreate, onUpdate, onDelete }) {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const blank = {
    id: "",
    name: "",
    description: "",
    parameters: [],
    // a formátumok NEM szerkeszthetők kézzel – a sablonokból jönnek
    queryText: "",
    upload: { file: null, extension: "" } // extension automatikus
  };
  const [draft, setDraft] = useState(blank);

  const openCreate = () => {
    setEditingId(null);
    setDraft(blank);
    setShowModal(true);
  };

  const openEdit = (tpl) => {
    setEditingId(String(tpl.id));
    setDraft({
      id: String(tpl.id),
      name: tpl.name || "",
      description: tpl.description || "",
      parameters: JSON.parse(JSON.stringify(tpl.parameters || [])),
      queryText: tpl._rawReport?.query || "",
      upload: { file: null, extension: "" }
    });
    setShowModal(true);
  };

  const addParam = () => {
    setDraft((d) => ({
      ...d,
      parameters: [
        ...(d.parameters || []),
        {
          key: `p${(d.parameters || []).length + 1}`,
          type: "text",
          label: "",
          default: ""
        }
      ]
    }));
  };
  const updateParam = (i, patch) => {
    setDraft((d) => {
      const arr = [...(d.parameters || [])];
      arr[i] = { ...arr[i], ...patch };
      if (patch.type && !["enum", "list"].includes(patch.type))
        arr[i].options = undefined;
      if (patch.type && patch.type === "list" && !Array.isArray(arr[i].default))
        arr[i].default = [];
      if (patch.type && patch.type === "checkbox")
        arr[i].default = !!arr[i].default;
      return { ...d, parameters: arr };
    });
  };
  const removeParam = (i) => {
    setDraft((d) => {
      const arr = [...(d.parameters || [])];
      arr.splice(i, 1);
      const relabeled = arr.map((p, idx) => ({ ...p, key: `p${idx + 1}` }));
      return { ...d, parameters: relabeled };
    });
  };

  const saveTemplate = async () => {
    if (!draft.name) {
      alert("Név kötelező.");
      return;
    }
    if (!draft.queryText) {
      alert("A lekérdezés szövege kötelező.");
      return;
    }

    const inferredExt = draft.upload?.file
      ? inferFormatFromFilename(draft.upload.file.name)
      : "";

    // korábbi formátumok + sablon formátumok egyesítése
    const currentlyEditing = templates.find(
      (t) => String(t.id) === String(editingId)
    );
    //    const extSet = new Set(["CSV", "XLSX"]);
    const extSet = new Set(["XLSX"]);
    (currentlyEditing?.formats || []).forEach((f) =>
      extSet.add(String(f).toUpperCase())
    );
    (currentlyEditing?.templateFormats || []).forEach((f) =>
      extSet.add(String(f).toUpperCase())
    );
    if (inferredExt) extSet.add(inferredExt.toUpperCase());

    const payload = {
      report: {
        name: draft.name,
        description: draft.description,
        parameterType: buildParameterTypeString(draft.parameters),
        parameterName: buildParameterNameString(draft.parameters),
        parameterDefaultValue: buildParameterDefaultString(draft.parameters),
        enumListPairs: buildEnumPairsString(draft.parameters),
        query: draft.queryText,
        // ⬇️ mostantól a teljes uniót küldjük vissza
        extensions: Array.from(extSet).join(";")
      },
      templateFile: draft.upload?.file || null,
      templateExtension: inferredExt
    };

    if (editingId) {
      await onUpdate?.(editingId, payload);
    } else {
      await onCreate?.(payload);
    }
    setShowModal(false);
  };

  const handleTemplateDownload = async (tplFileId) => {
    try {
      const { blob, filename } = await reportApi.downloadReportTemplateFileApi(
        tplFileId
      );
      downloadBlob(blob, filename);
    } catch (e) {
      console.error("Sablon letöltés hiba:", {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message || e
      });
      alert("Nem sikerült a letöltés.");
    }
  };

  const handleTemplateDelete = async (tplFileId) => {
    if (!window.confirm("Biztosan törlöd ezt a sablonfájlt?")) return;
    try {
      await dispatch(reportApi.deleteReportTemplateFileApi(tplFileId));
    } catch (e) {
      console.error("Sablon törlés hiba:", e);
      alert("Nem sikerült a törlés.");
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Card.Title className="mb-3">
          <i className="bi bi-gear-wide-connected me-2" />
          Report designer (admin)
        </Card.Title>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="text-muted">
            Sablonok kezelése: név, leírás, paraméterek, lekérdezés és
            sablonfájl.
          </div>
          <Button onClick={openCreate}>
            <i className="bi bi-plus-lg me-1" />
            Új sablon
          </Button>
        </div>

        <Table hover responsive size="sm" className="mb-0">
          <thead>
            <tr>
              <th>ID</th>
              <th>Név</th>
              <th>Leírás</th>
              <th>Paraméterek</th>
              <th>Formátumok</th>
              <th className="text-end">Műveletek</th>
            </tr>
          </thead>
          <tbody>
            {(templates || []).map((t, i) => {
              const rowKey = t?.id != null ? `tpl-${t.id}` : `tpl-new-${i}`;
              const fmts = computeAvailableFormats(t);
              return (
                <tr key={rowKey}>
                  <td>
                    <code>{rowKey}</code>
                  </td>
                  <td>{t.name}</td>
                  <td className="text-truncate" style={{ maxWidth: 280 }}>
                    {t.description}
                  </td>
                  <td>
                    {(t.parameters || []).map((p) => (
                      <Badge
                        bg="light"
                        text="dark"
                        key={p.key}
                        className="me-1"
                      >
                        {p.label}{" "}
                        <small className="text-muted">({p.type})</small>
                      </Badge>
                    ))}
                  </td>
                  <td>
                    {fmts.map((f) => (
                      <Badge bg="secondary" key={f} className="me-1">
                        {f}
                      </Badge>
                    ))}
                  </td>
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-2"
                      onClick={() => openEdit(t)}
                    >
                      <i className="bi bi-pencil-square" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => onDelete?.(t.id)}
                    >
                      <i className="bi bi-trash" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingId ? "Sablon szerkesztése" : "Új sablon"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Label>Név</Form.Label>
                <Form.Control
                  placeholder="Sablon megnevezés"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                />
              </Col>

              <Col md={6}>
                <Form.Label>Elérhető formátumok (automatikus)</Form.Label>
                <div>
                  {(() => {
                    const current = templates.find(
                      (t) => String(t.id) === String(editingId)
                    );
                    //                    const fmts = new Set(["CSV", "XLSX"]);
                    const fmts = new Set(["XLSX"]);
                    (current?.formats || []).forEach((f) =>
                      fmts.add(String(f).toUpperCase())
                    );
                    (current?.templateFormats || []).forEach((f) =>
                      fmts.add(String(f).toUpperCase())
                    );
                    if (draft.upload?.file) {
                      const inf = inferFormatFromFilename(
                        draft.upload.file.name
                      );
                      if (inf) fmts.add(inf.toUpperCase());
                    }
                    if (fmts.has("HTML")) fmts.add("PDF");
                    return Array.from(fmts).map((f) => (
                      <Badge key={f} bg="secondary" className="me-1">
                        {f}
                      </Badge>
                    ));
                  })()}
                </div>
              </Col>

              <Col md={12}>
                <Form.Label>Leírás</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  placeholder="Rövid leírás"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, description: e.target.value }))
                  }
                />
              </Col>

              <Col md={12}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Form.Label className="m-0">Paraméterek</Form.Label>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={addParam}
                  >
                    <i className="bi bi-plus-lg me-1" />
                    Új paraméter
                  </Button>
                </div>

                {(draft.parameters || []).map((p, i) => (
                  <Card key={i} className="mb-2">
                    <Card.Body>
                      <Row className="g-2 align-items-end">
                        <Col md={3}>
                          <Form.Label>Kulcs</Form.Label>
                          <Form.Control
                            placeholder="pl. p1"
                            value={p.key}
                            onChange={(e) =>
                              updateParam(i, { key: e.target.value.trim() })
                            }
                          />
                        </Col>
                        <Col md={3}>
                          <Form.Label>Típus</Form.Label>
                          <Form.Select
                            value={p.type}
                            onChange={(e) =>
                              updateParam(i, { type: e.target.value })
                            }
                          >
                            {PARAM_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Label>Címke</Form.Label>
                          <Form.Control
                            placeholder="pl. Időszak"
                            value={p.label || ""}
                            onChange={(e) =>
                              updateParam(i, { label: e.target.value })
                            }
                          />
                        </Col>
                        <Col md={2} className="text-end">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removeParam(i)}
                          >
                            <i className="bi bi-x-lg" />
                          </Button>
                        </Col>
                      </Row>

                      {(p.type === "enum" || p.type === "list") && (
                        <div className="mt-2">
                          <Form.Label>Opciók</Form.Label>
                          {(p.options || [{ id: "any", label: "Mind" }]).map(
                            (opt, oi) => (
                              <InputGroup className="mb-1" key={oi}>
                                <Form.Control
                                  placeholder="azonosító (id)"
                                  value={opt.id}
                                  onChange={(e) => {
                                    const options = [...(p.options || [])];
                                    options[oi] = {
                                      ...opt,
                                      id: e.target.value
                                    };
                                    updateParam(i, { options });
                                  }}
                                />
                                <Form.Control
                                  placeholder="címke (label)"
                                  value={opt.label}
                                  onChange={(e) => {
                                    const options = [...(p.options || [])];
                                    options[oi] = {
                                      ...opt,
                                      label: e.target.value
                                    };
                                    updateParam(i, { options });
                                  }}
                                />
                                <Button
                                  variant="outline-danger"
                                  onClick={() => {
                                    const options = [...(p.options || [])];
                                    options.splice(oi, 1);
                                    updateParam(i, { options });
                                  }}
                                >
                                  <i className="bi bi-trash" />
                                </Button>
                              </InputGroup>
                            )
                          )}
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => {
                              const options = [...(p.options || [])];
                              options.push({ id: "", label: "" });
                              updateParam(i, { options });
                            }}
                          >
                            + Opció
                          </Button>
                        </div>
                      )}

                      <div className="mt-2">
                        <Form.Label>
                          Alapértelmezett érték (opcionális)
                        </Form.Label>
                        {p.type === "checkbox" ? (
                          <div>
                            <Form.Check
                              type="checkbox"
                              label="Bejelölve"
                              checked={!!p.default}
                              onChange={(e) =>
                                updateParam(i, { default: e.target.checked })
                              }
                            />
                          </div>
                        ) : p.type === "date" ? (
                          <Form.Control
                            type="date"
                            value={p.default ?? ""}
                            onChange={(e) =>
                              updateParam(i, { default: e.target.value })
                            }
                          />
                        ) : p.type === "number" ? (
                          <Form.Control
                            type="number"
                            value={p.default ?? ""}
                            onChange={(e) =>
                              updateParam(i, {
                                default:
                                  e.target.value === ""
                                    ? ""
                                    : Number(e.target.value)
                              })
                            }
                          />
                        ) : p.type === "enum" ? (
                          <Form.Select
                            value={p.default ?? ""}
                            onChange={(e) =>
                              updateParam(i, { default: e.target.value })
                            }
                          >
                            <option value="">(nincs)</option>
                            {(p.options || []).map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        ) : p.type === "list" ? (
                          <Form.Select
                            multiple
                            value={Array.isArray(p.default) ? p.default : []}
                            onChange={(e) =>
                              updateParam(i, {
                                default: Array.from(
                                  e.target.selectedOptions
                                ).map((o) => o.value)
                              })
                            }
                          >
                            {(p.options || []).map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        ) : p.type === "dateRange" ||
                          p.type === "numberRange" ? (
                          <Row className="g-2">
                            <Col>
                              <Form.Control
                                type={
                                  p.type === "dateRange" ? "date" : "number"
                                }
                                placeholder="from"
                                value={p.default?.from ?? ""}
                                onChange={(e) =>
                                  updateParam(i, {
                                    default:
                                      p.type === "numberRange"
                                        ? {
                                            ...p.default,
                                            from:
                                              e.target.value === ""
                                                ? ""
                                                : Number(e.target.value)
                                          }
                                        : { ...p.default, from: e.target.value }
                                  })
                                }
                              />
                            </Col>
                            <Col>
                              <Form.Control
                                type={
                                  p.type === "dateRange" ? "date" : "number"
                                }
                                placeholder="to"
                                value={p.default?.to ?? ""}
                                onChange={(e) =>
                                  updateParam(i, {
                                    default:
                                      p.type === "numberRange"
                                        ? {
                                            ...p.default,
                                            to:
                                              e.target.value === ""
                                                ? ""
                                                : Number(e.target.value)
                                          }
                                        : { ...p.default, to: e.target.value }
                                  })
                                }
                              />
                            </Col>
                          </Row>
                        ) : (
                          <Form.Control
                            placeholder="pl. any / 0 / szöveg…"
                            value={p.default ?? ""}
                            onChange={(e) =>
                              updateParam(i, { default: e.target.value })
                            }
                          />
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </Col>

              <Col md={12}>
                <Form.Label>Lekérdezés</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={6}
                  placeholder="SELECT ... WHERE created BETWEEN :p1_from AND :p1_to AND (:p3 = 'any' OR status = :p3) AND amount BETWEEN :p4_from AND :p4_to AND status IN (:p5)"
                  value={draft.queryText}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, queryText: e.target.value }))
                  }
                />
                <div className="text-muted mt-1" style={{ fontSize: ".85rem" }}>
                  Használd a <code>:pN</code> vagy tartományhoz{" "}
                  <code>:pN_from</code>, <code>:pN_to</code> paramétereket.
                  Többválasztós listához <code>IN (:pN)</code>.
                </div>
              </Col>

              <Col md={6}>
                <Form.Label>Sablonfájl (opcionális)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx,.xls,.csv,.pdf,.html,.htm,.xml"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setDraft((d) => ({
                      ...d,
                      upload: {
                        file,
                        extension: file
                          ? inferFormatFromFilename(file.name)
                          : ""
                      }
                    }));
                  }}
                />
                {draft.upload?.file ? (
                  <div
                    className="text-muted mt-1"
                    style={{ fontSize: ".9rem" }}
                  >
                    Észlelt formátum:{" "}
                    <strong>{draft.upload.extension || "ismeretlen"}</strong>
                  </div>
                ) : null}
              </Col>
              {/* Feltöltött sablonok */}
              <Col md={12}>
                <Form.Label>Feltöltött sablonok</Form.Label>
                {(() => {
                  const current = templates.find(
                    (t) => String(t.id) === String(editingId)
                  );
                  const list = current?._templates || [];
                  if (!list.length) {
                    return (
                      <div className="text-muted">
                        Ehhez a reporthoz még nincs sablon feltöltve.
                      </div>
                    );
                  }
                  return (
                    <Table responsive hover size="sm" className="mb-2">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Kiterjesztés</th>
                          <th>Fájlnév</th>
                          <th>Tartalomtípus</th>
                          <th className="text-end">Műveletek</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((t, i) => {
                          const rowKey =
                            t?.templateId != null
                              ? `tplfile-${t.templateId}`
                              : `tplfile-${t.fileName || t.extension || i}`;
                          console.log("t: ", t);
                          return (
                            <tr key={rowKey}>
                              <td>{t.templateId}</td>
                              <td>{t.extension}</td>
                              <td
                                className="text-truncate"
                                style={{ maxWidth: 280 }}
                              >
                                {t.fileName}
                              </td>
                              <td>{t.contentType || "-"}</td>
                              <td className="text-end">
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="me-2"
                                  onClick={() =>
                                    handleTemplateDownload(t.templateId)
                                  }
                                >
                                  <i className="bi bi-download" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() =>
                                    handleTemplateDelete(t.templateId)
                                  }
                                >
                                  <i className="bi bi-trash" />
                                </Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  );
                })()}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Mégse
            </Button>
            <Button onClick={saveTemplate}>Mentés</Button>
          </Modal.Footer>
        </Modal>
      </Card.Body>
    </Card>
  );
}

/* ------------------------------- KPI dummy ------------------------------- */
/*
function KpiAndSamples() {
  const kpis = {
    totalWorks: 128,
    completedWorks: 92,
    scheduledToday: 11,
    totalBoards: 347.5
  };
  const monthlyWorks = [
    { month: "Jan", count: 18 },
    { month: "Feb", count: 21 },
    { month: "Már", count: 25 },
    { month: "Ápr", count: 27 },
    { month: "Máj", count: 22 },
    { month: "Jún", count: 15 }
  ];
  const maxMonthly = Math.max(...monthlyWorks.map((m) => m.count));

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Összes munka</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {kpis.totalWorks}
              </div>
              <Badge bg="secondary">YTD</Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Kész munkák</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {kpis.completedWorks}
              </div>
              <small className="text-success">
                <i className="bi bi-check2-circle me-1" />
                72%
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Ma ütemezve</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {kpis.scheduledToday}
              </div>
              <small className="text-primary">
                <i className="bi bi-calendar2-week me-1" />
                mai nap
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Összes tábla</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {kpis.totalBoards}
              </div>
              <small className="text-muted">felhasználva (becslés)</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Havi munkaszám</Card.Title>
              {monthlyWorks.map((m) => (
                <div key={m.month} className="mb-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{m.month}</span>
                    <span>
                      <strong>{m.count}</strong>
                    </span>
                  </div>
                  <ProgressBar now={(m.count / maxMonthly) * 100} />
                </div>
              ))}
              <div className="mt-3 text-muted" style={{ fontSize: ".9rem" }}>
                Egyszerű vizualizáció — később mehet tényleges grafikon.
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Top felhasználók</Card.Title>
              <Table hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Felhasználó</th>
                    <th className="text-end">Munkák</th>
                    <th className="text-end">Táblák</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: "Kiss Béla", works: 34, boards: 86.5 },
                    { name: "Nagy Anna", works: 29, boards: 78.0 },
                    { name: "Tóth Ádám", works: 25, boards: 66.0 }
                  ].map((u) => (
                    <tr key={u.name}>
                      <td>{u.name}</td>
                      <td className="text-end">{u.works}</td>
                      <td className="text-end">{u.boards}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}*/
function KpiAndSamples() {
  // Adatok a store-ból
  const works = useSelector((s) => s.works || []);
  const createdTables = useSelector((s) => s.createdTables || []);
  const orderedWorkIds = useMemo(() => {
    return new Set(
      (works || [])
        .filter((w) => !!w?.isOrdered)
        .map((w) => w.workId ?? w.id)
        .filter((id) => id != null)
    );
  }, [works]);

  const tablesByWorkId = useMemo(() => {
    if (!Array.isArray(createdTables) || createdTables.length === 0)
      return null;
    const map = new Map();
    for (const ct of createdTables) {
      const wid =
        ct.work?.workId ?? ct.workId ?? ct.work_id ?? ct.workID ?? null;
      if (wid == null) continue;
      if (!orderedWorkIds.has(wid)) continue; // csak isOrdered munkák
      map.set(wid, (map.get(wid) || 0) + 1);
    }
    return map;
  }, [createdTables, orderedWorkIds]);
  console.log("Ordered work IDs:", orderedWorkIds);
  console.log("Works data:", works);
  // --- Helpers
  const monthNames = [
    "Jan",
    "Feb",
    "Már",
    "Ápr",
    "Máj",
    "Jún",
    "Júl",
    "Aug",
    "Szep",
    "Okt",
    "Nov",
    "Dec"
  ];
  const parseDate = (v) => (v ? new Date(v) : null);
  const sameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const primaryDateOfWork = (w) =>
    parseDate(w.companyFinishDate) ||
    parseDate(w.orderDate) ||
    parseDate(w.scheduleDate) ||
    null;
  const statusOf = (w) =>
    String(w.companyStatus || w.status || "").toUpperCase();
  const isCompleted = (w) => statusOf(w).includes("COMPLETED");
  const boardsForWork = (w) => {
    const wid = w.workId ?? w.id;
    const fromCT = tablesByWorkId?.get(wid);
    if (typeof fromCT === "number") return fromCT; // pontos: createdTables alapján
    // Fallback: csak megrendelt munkáknál vesszük a work mezőt
    if (!w?.isOrdered) return 0;
    return Number(w.usedTables) || Number(w.table_usage) || 0;
  };

  // --- KPI-k
  const totalWorks = works.length;
  const completedWorks = works.filter(isCompleted).length;
  const today = new Date();
  const scheduledToday = works.filter((w) =>
    sameDay(parseDate(w.scheduleDate), today)
  ).length;

  const totalBoards = tablesByWorkId
    ? Array.from(tablesByWorkId.values()).reduce((a, b) => a + b, 0)
    : works
        .filter((w) => !!w?.isOrdered)
        .reduce((acc, w) => acc + boardsForWork(w), 0);

  // Havi munkaszám (utolsó 6 hónap a primaryDate alapján)
  const monthlyWorks = (() => {
    const buckets = new Map(); // key: yyyy-mm, val: count
    works.forEach((w) => {
      const d = primaryDateOfWork(w);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      buckets.set(key, (buckets.get(key) || 0) + 1);
    });
    // utolsó 6 hónap kronologikusan
    const result = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const ref = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${ref.getFullYear()}-${String(ref.getMonth() + 1).padStart(
        2,
        "0"
      )}`;
      result.push({
        month: monthNames[ref.getMonth()],
        count: buckets.get(key) || 0
      });
    }
    return result;
  })();
  const maxMonthly = Math.max(1, ...monthlyWorks.map((m) => m.count)); // \o/ ne legyen 0-val osztás

  // Top felhasználók (work szám + becsült tábla darab)
  const topUsers = (() => {
    const byUser = new Map(); // name -> { works, boards }
    works.forEach((w) => {
      const name = w.user?.name || w.client?.name || w.username || "Ismeretlen";
      const prev = byUser.get(name) || { works: 0, boards: 0 };
      prev.works += 1;
      prev.boards += boardsForWork(w);
      byUser.set(name, prev);
    });
    return Array.from(byUser.entries())
      .map(([name, v]) => ({ name, works: v.works, boards: v.boards }))
      .sort((a, b) => b.works - a.works || b.boards - a.boards)
      .slice(0, 5);
  })();

  return (
    <>
      <Row className="g-3 mb-4">
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Összes munka</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {totalWorks}
              </div>
              <Badge bg="secondary">YTD</Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Kész munkák</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {completedWorks}
              </div>
              <small className="text-success">
                <i className="bi bi-check2-circle me-1" />
                {totalWorks
                  ? Math.round((completedWorks / totalWorks) * 100)
                  : 0}
                %
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Ma ütemezve</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {scheduledToday}
              </div>
              <small className="text-primary">
                <i className="bi bi-calendar2-week me-1" />
                mai nap
              </small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <div className="text-muted">Összes tábla</div>
              <div style={{ fontSize: "1.8rem", fontWeight: 700 }}>
                {totalBoards}
              </div>
              <small className="text-muted">felhasználva (becslés)</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Havi munkaszám</Card.Title>
              {monthlyWorks.map((m) => (
                <div key={m.month} className="mb-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-muted">{m.month}</span>
                    <span>
                      <strong>{m.count}</strong>
                    </span>
                  </div>
                  <ProgressBar now={(m.count / maxMonthly) * 100} />
                </div>
              ))}
              <div className="mt-3 text-muted" style={{ fontSize: ".9rem" }}>
                Az utolsó 6 hónap a (befejezés → rendelés → ütemezés) dátum
                alapján.
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} lg={6}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3">Top felhasználók</Card.Title>
              <Table hover responsive size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>Felhasználó</th>
                    <th className="text-end">Munkák</th>
                    <th className="text-end">Táblák</th>
                  </tr>
                </thead>
                <tbody>
                  {topUsers.length ? (
                    topUsers.map((u) => (
                      <tr key={u.name}>
                        <td>{u.name}</td>
                        <td className="text-end">{u.works}</td>
                        <td className="text-end">{u.boards}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-muted">
                        Nincs adat.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
}
/* ------------------------------- Page root ------------------------------- */

function ReportsPage() {
  const dispatch = useDispatch();
  const role = useSelector((s) => s.auth?.user?.role);
  const isAdmin = role === "admin" || role === "companyAdmin";
  const templates = useSelector((s) => s.reports?.templates || []);
  const loading = useSelector((s) => s.reports?.loading);
  console.log("templates: (begin)", templates);
  useEffect(() => {
    reportApi.getAllReportTemplatesApi();
  }, [dispatch]);

  const handleExport = async (templateId, format, params) => {
    try {
      // normalizálás: a backend mindig a megfelelő struktúrát kapja
      const tpl =
        templates.find((t) => String(t.id) === String(templateId)) || {};
      const normalized = {};
      (tpl.parameters || []).forEach((p) => {
        const v = params?.[p.key];
        if (p.type === "dateRange" || p.type === "numberRange") {
          normalized[p.key] =
            v && typeof v === "object"
              ? { from: v.from ?? "", to: v.to ?? "" }
              : { from: "", to: "" };
        } else if (p.type === "list") {
          const arr = Array.isArray(v) ? v : [];
          normalized[p.key] = arr;
          // fontos: a backend SQL ilyen feltételt használ: (:pN_empty = TRUE OR ... IN (:pN))
          normalized[`${p.key}_empty`] = arr.length === 0;
        } else if (p.type === "checkbox") {
          normalized[p.key] = !!v;
        } else {
          normalized[p.key] = v ?? "";
        }
      });

      const { blob, filename } = await reportApi.runReportApi(
        templateId,
        format,
        normalized
      );
      downloadBlob(blob, filename);
    } catch (err) {
      console.error("Report export hiba:", err);
      alert("Nem sikerült az export.");
    }
  };

  const handleCreate = async (tplPayload) => {
    await dispatch(reportApi.createReportTemplateApi(tplPayload));
  };

  const handleUpdate = async (id, tplPayload) => {
    await dispatch(reportApi.updateReportTemplateApi(id, tplPayload));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Biztosan törlöd ezt a sablont?")) return;
    await dispatch(reportApi.deleteReportTemplateApi(id));
  };

  return (
    <Container fluid style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
      {loading ? (
        <div className="d-flex align-items-center gap-2 mb-3">
          <Spinner size="sm" />
          <span>Betöltés…</span>
        </div>
      ) : null}

      {<KpiAndSamples />}

      <ReportExportPanel templates={templates} onExport={handleExport} />

      {isAdmin && (
        <AdminReportDesigner
          templates={templates}
          onCreate={handleCreate}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </Container>
  );
}

export default ReportsPage;
